import React from "react";
import { createUseStyles } from "react-jss";
import jssVariables from "../../../../jss_setup";
import { TranslationContext } from "../../../../umbilical_tools";
import { useContext } from "react";
import PropTypes from "prop-types";
import { verticalTableStyles } from ".";
import { getProductPax } from "./dataGetters";

const paxDetailsMobileStyles = createUseStyles({
  PaxDetailsMobile: (props) => ({
    display: "grid",
    gridGap: jssVariables.double_gap,
    ...(props?.customStyles?.PaxDetailsMobile ?? {}),
  }),
  card: (props) => ({
    padding: jssVariables.normal_gap,
    boxShadow: jssVariables.shadows.floatingShadow,
    borderRadius: jssVariables.border_radius,
    background: "rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    ...(props?.customStyles?.card ?? {}),
  }),
  header: (props) => ({
    display: "grid",
    gridTemplateColumns: `${jssVariables.normal_gap} 1fr max-content`,
    fontWeight: 500,
    textTransform: "capitalize",
    ...(props?.customStyles?.header ?? {}),
  }),
  table: (props) => ({
    width: "100%",
    "& tr": { verticalAlign: "baseline" },
    "& td": {
      padding: `calc(${jssVariables.normal_gap} / 4) 0`,
      fontWeight: 500,
    },
    ...(props?.customStyles?.table ?? {}),
  }),
  tHeader: (props) => ({
    ...verticalTableStyles.tHeader,
    background: "none",
    width: "30%",
    ...(props?.customStyles?.tHeader ?? {}),
  }),
  divider: (props) => ({
    height: "1px",
    background: jssVariables.colors.base_color,
    margin: `${jssVariables.half_gap} 0`,
    ...(props?.customStyles?.divider ?? {}),
  }),
  baseColorText: (props) => ({
    color: jssVariables.colors.base_color,
    ...(props?.customStyles?.baseColorText ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    PaxDetailsMobile: (props) => ({
      display: "none",
      ...(props?.customStyles?.PaxDetailsMobile?.smallscreen ?? {}),
    }),
  },
});
const PaxDetailsMobile = ({ productType, reservation }) => {
  const { t } = useContext(TranslationContext);

  const classes = paxDetailsMobileStyles({});
  const allPax = getProductPax({ productType, reservation, t });

  return (
    <div className={classes.PaxDetailsMobile}>
      {allPax.map((pax, idx) => (
        <div className={classes.card} key={idx}>
          <div className={classes.header}>
            <span className={classes.baseColorText}># </span> {t("guest")}{" "}
            {idx + 1}
            {pax.find((p) => p[0] === "leadGuest")[1] && (
              <strong>{t("leadGuest")}</strong>
            )}
          </div>
          <div className={classes.divider} />
          <table className={classes.table}>
            <tbody>
              {pax.map((field, index) => (
                <tr key={index}>
                  {field[1] && field[0] !== "leadGuest" ? (
                    <React.Fragment>
                      <td className={classes.tHeader}>{field[0]}:</td>
                      <td>{field[1]}</td>
                    </React.Fragment>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </div>
  );
};
PaxDetailsMobile.propTypes = {
  productType: PropTypes.string.isRequired,
  reservation: PropTypes.object.isRequired,
};

const paxDetailsDesktopStyles = createUseStyles({
  PaxDetailsDesktop: (props) => ({
    display: "none",
    padding: jssVariables.double_gap,
    boxShadow: jssVariables.shadows.floatingShadow,
    borderRadius: jssVariables.border_radius,
    background: "rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    ...(props?.customStyles?.PaxDetailsDesktop ?? {}),
  }),

  header: (props) => ({
    fontWeight: "bold",
    textTransform: "capitalize",
    fontSize: "large",
    textAlign: "center",
    paddingBottom: jssVariables.normal_gap,
    ...(props?.customStyles?.header ?? {}),
  }),
  table: (props) => ({
    width: "100%",
    "& th:first-child": {
      borderRadius: [jssVariables.normal_gap, 0, 0, jssVariables.normal_gap],
    },
    "& th:last-child": {
      borderRadius: [0, jssVariables.normal_gap, jssVariables.normal_gap, 0],
    },
    "& th": { padding: jssVariables.normal_gap },
    "& td:not(:first-child)": { borderLeft: "1px solid #cccccc" },
    "& td": {
      padding: [jssVariables.half_gap, jssVariables.normal_gap],
      color: jssVariables.colors.text.dark,
      fontSize: "large",
      textAlign: "center",
    },
    ...(props?.customStyles?.table ?? {}),
  }),
  thead: (props) => ({
    background: "#F7F7F7",
    textTransform: "capitalize",
    fontWeight: 500,
    color: jssVariables.colors.base_color,
    padding: jssVariables.half_gap,
    borderRadius: jssVariables.normal_gap,
    ...(props?.customStyles?.thead ?? {}),
  }),
  divider: (props) => ({
    height: "1px",
    background: jssVariables.colors.base_color,
    margin: `${jssVariables.half_gap} 0`,
    ...(props?.customStyles?.divider ?? {}),
  }),
  baseColorText: (props) => ({
    color: jssVariables.colors.base_color,
    ...(props?.customStyles?.baseColorText ?? {}),
  }),
  emptyDivider: (props) => ({
    height: jssVariables.normal_gap,
    width: "100%",
    background: "transparent",
    ...(props?.customStyles?.emptyDivider ?? {}),
  }),
  [`@media ${jssVariables.media.smallscreen}`]: {
    PaxDetailsDesktop: (props) => ({
      display: "grid",
      ...(props?.customStyles?.PaxDetailsDesktop?.smallscreen ?? {}),
    }),
  },
});
const PaxDetailsDesktop = ({ productType, reservation }) => {
  const { t } = useContext(TranslationContext);

  const classes = paxDetailsDesktopStyles({});
  const allPax = getProductPax({ productType, reservation, t });

  return (
    allPax.length > 0 && (
      <div className={classes.PaxDetailsDesktop}>
        <div className={classes.header}>{t("guests")}</div>
        <table className={classes.table}>
          <thead className={classes.thead}>
            <tr>
              <th>#</th>
              {allPax[0].map((dat, idx) => (
                <th key={idx}>{dat[0] === "leadGuest" ? t(dat[0]) : dat[0]}</th>
              ))}
            </tr>
          </thead>
          <tbody className={classes.tbody}>
            <tr className={classes.emptyDivider} />
            {allPax.map((pax, idx) => (
              <tr key={idx}>
                <td>{idx + 1}</td>
                {pax.map((datum, index) => (
                  <td key={index}>{datum[1]}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    )
  );
};
PaxDetailsDesktop.defaultProps = { productType: "", reservation: {} };
PaxDetailsDesktop.propTypes = {
  productType: PropTypes.string.isRequired,
  reservation: PropTypes.object.isRequired,
};

const PaxDetails = ({ productType, reservation }) => {
  return (
    <React.Fragment>
      <PaxDetailsMobile productType={productType} reservation={reservation} />
      <PaxDetailsDesktop productType={productType} reservation={reservation} />
    </React.Fragment>
  );
};
PaxDetails.defaultProps = { productType: "", reservation: {} };
PaxDetails.propTypes = {
  productType: PropTypes.string.isRequired,
  reservation: PropTypes.object.isRequired,
};
export default PaxDetails;
